import React from 'react';

import trackingKeys from '@olxeu-a2b/delivery-utils/src/tracking/trackingKeys';
import ReturnsAdBlock from './ReturnsAdBlock';
import ReturnsActionInfoBlock from './ReturnsActionInfoBlock';
import ReturnsDesktopTransaction from './ReturnsDesktopTransaction';
import useGetUserType from '../../hooks/useGetUserType';
import {
  ClaimOverviewWithReturnsDto,
  PaymentInfo,
  ReturnItemDetails,
} from '../../../../../typings/returns';
import { USER_TYPE } from '../../constants';

import S from './style/ReturnsAndClaimsCardContent';
import ReturnsReport from './ReturnsReport';

interface ReturnsAndClaimsCardContentProps {
  onFillDataClick: (returnId: string, userType: USER_TYPE) => void;
  isDesktop: boolean;
  showReturnsTransactionInfo: boolean;
  goToReturnDetails?: (returnId: string) => void;
  goToOrderDetails?: (orderId: string, isSeller: boolean) => void;
  claimItem?: ClaimOverviewWithReturnsDto;
  returnItemDetails?: ReturnItemDetails;
  refetch?: PaymentInfo;
}

const ReturnsAndClaimsCardContent: React.FC<ReturnsAndClaimsCardContentProps> = ({
  returnItemDetails,
  claimItem,
  goToReturnDetails,
  goToOrderDetails,
  isDesktop,
  showReturnsTransactionInfo,
  onFillDataClick,
  refetch,
}) => {
  const isThisReturnDetails = !!returnItemDetails;
  const isThisClaimWithReturn = !!claimItem?.returnEntries?.length;
  const isThisClaim = !isThisReturnDetails && !isThisClaimWithReturn;
  const returnEntries = claimItem?.returnEntries;
  const { isSeller } = useGetUserType({
    buyerId: isThisReturnDetails ? returnItemDetails.buyerInfo.id : claimItem.adDetails.buyer.id,
    sellerId: isThisReturnDetails ? returnItemDetails.sellerInfo.id : claimItem.adDetails.seller.id,
  });
  const userData = () => {
    if (isThisReturnDetails) {
      return isSeller ? returnItemDetails.buyerInfo : returnItemDetails.sellerInfo;
    }
    return isSeller ? claimItem.adDetails.buyer : claimItem.adDetails.seller;
  };
  const trackingExtras = {
    seller_type: isSeller ? USER_TYPE.SELLER : USER_TYPE.BUYER,
    ad_id: isThisReturnDetails ? returnItemDetails.adInfo.id.toString() : claimItem.adId,
    seller_id: isThisReturnDetails
      ? returnItemDetails.sellerInfo.id
      : claimItem.adDetails.seller.id,
    touch_point_page: trackingKeys.touchPointPages.deliverySdOverview,
  };

  return (
    <div data-cy="returns-card-content">
      <S.TopBlock>
        {isThisReturnDetails ? (
          <ReturnsAdBlock
            returnId={returnItemDetails.id}
            status={returnItemDetails.presentation.statusKey}
            title={returnItemDetails.adInfo.title}
            price={returnItemDetails.adInfo.price.amount}
            currency={returnItemDetails.adInfo.price.currency}
            actionDeadline={returnItemDetails.actionDeadline}
            showRedirectionStyles={false}
            image={returnItemDetails.adInfo.image}
            onAdPreviewClick={() => goToOrderDetails(returnItemDetails.order.id, isSeller)}
            postingDeadlineStart={returnItemDetails.delivery?.posting?.window?.start}
            postingDeadlineEnd={returnItemDetails.delivery?.posting?.window?.end}
          />
        ) : (
          <ReturnsAdBlock
            returnId={isThisClaimWithReturn ? returnEntries[0].id : null}
            status={
              isThisClaimWithReturn
                ? returnEntries[0].presentation.statusKey
                : claimItem.presentation.statusKey
            }
            title={claimItem.adDetails.title}
            price={claimItem.adDetails.price.cents}
            currency={claimItem.adDetails.price.currency}
            actionDeadline={
              isThisClaimWithReturn ? returnEntries[0].actionDeadline : claimItem.actionDeadline
            }
            timeToNegotiate={claimItem.presentation.config.timeToNegotiateHours}
            showRedirectionStyles
            image={claimItem.adDetails.imageUrl}
            onAdPreviewClick={() =>
              isThisClaimWithReturn
                ? goToReturnDetails(returnEntries[0].id)
                : goToOrderDetails(claimItem.orderId, isSeller)
            }
            postingDeadlineStart={
              isThisClaimWithReturn && returnEntries[0]?.delivery?.postingWindowOverview?.start
            }
            postingDeadlineEnd={
              isThisClaimWithReturn && returnEntries[0]?.delivery?.postingWindowOverview?.end
            }
          />
        )}
      </S.TopBlock>
      {showReturnsTransactionInfo && (
        <S.BottomBlock>
          <ReturnsDesktopTransaction userData={userData()} isSeller={isSeller} />
        </S.BottomBlock>
      )}
      {isThisClaim && (
        <ReturnsReport
          orderId={claimItem.orderId}
          fileName={'attachments' in claimItem ? claimItem.attachments[0] : ''}
          text={claimItem.text}
          attachmentsCount={claimItem.attachments?.length ?? 0}
          isSeller={isSeller}
          trackingExtras={trackingExtras}
          isDesktop={isDesktop}
        />
      )}
      {isThisReturnDetails ? (
        <ReturnsActionInfoBlock
          onFillDataClick={onFillDataClick}
          returnId={returnItemDetails.id}
          adId={returnItemDetails.adInfo.id}
          status={returnItemDetails.presentation.statusKey}
          isDesktop={isDesktop}
          isSeller={isSeller}
          sellerId={returnItemDetails.sellerInfo.id}
          shouldShowPaymentBtn={
            isSeller ? returnItemDetails.delivery.paymentOverview.isPaymentLinkAvailable : false
          }
          refetch={refetch}
        />
      ) : (
        <ReturnsActionInfoBlock
          onFillDataClick={onFillDataClick}
          returnId={isThisClaimWithReturn ? returnEntries[0].id : ''}
          adId={claimItem.adId}
          status={
            isThisClaimWithReturn
              ? returnEntries[0].presentation.statusKey
              : claimItem.presentation.statusKey
          }
          isDesktop={isDesktop}
          isSeller={isSeller}
          sellerId={claimItem.adDetails.seller.id}
          shouldShowPaymentBtn={false} // this button can only be visible in the details page
          refetch={refetch}
        />
      )}
    </div>
  );
};

export default ReturnsAndClaimsCardContent;
